import axios from 'axios';
import Qs from 'qs';

/**
 * API call to fetch aca questionnaire status
 *
 * @method fetchQuestionnaireStatus
 * @param   {number}    companyId
 * @return {Promise}
 */

export const fetchQuestionnaireStatusAPI = ({ ...params }) => {
    return axios({
        method: 'get',
        url: `/v2/questionnaires/status`,
        params,
        paramsSerializer(params) {
            return Qs.stringify(params, { arrayFormat: 'repeat' });
        },
    });
};

/**
 * API call to fetch aca questionnaire details
 *
 * @method fetchQuestionnaireDetails
 * @param   {number}    questionnaireId
 * @return {Promise}
 */

export const fetchQuestionnaireDetailsAPI = questionnaireId => {
    return axios({
        method: 'get',
        url: `/v2/questionnaires/${questionnaireId}`,
    });
};

/**
 * API call to fetch aca questionnaire information
 *
 * @method fetchQuestionnaireInfo
 * @return {Promise}
 */
export const fetchQuestionnaireInfoAPI = () => {
    return axios({
        method: 'get',
        url: `/v2/questionnaires?name=aca&isActive=true`,
    });
};

/**
 * API call to set aca questionnaire status
 *
 * @method setQuestionnaireStatus
 * @param   {number}    id
 * @param   {Object}    data
 * @return {Promise}
 */
export const setQuestionnaireStatusAPI = ({ questionnaireId, data }) => {
    return axios({
        method: 'POST',
        url: `/v2/questionnaires/${questionnaireId}/status`,
        data,
        headers: {
            'Content-Type': 'application/json',
        },
    });
};

/**
 * API call to update aca questionnaire status
 *
 * @method updateQuestionnaireStatus
 * @param   {number}    questionnaireId
 * @param   {number}    statusId
 * @param   {Object}    data
 * @return {Promise}
 */
export const updateQuestionnaireStatusAPI = ({ questionnaireId, statusId, data }) => {
    return axios({
        method: 'PUT',
        url: `/v2/questionnaires/${questionnaireId}/status/${statusId}`,
        data,
        headers: {
            'Content-Type': 'application/json',
        },
    });
};

/**
 * API call to fetch all questions
 *
 * @method fetchQuestionsAPI
 * @param   {number}    id
 * @param   {number}    year
 * @return {Promise}
 */
export const fetchQuestionsAPI = ({ questionnaireId, year }) => {
    return axios({
        method: 'get',
        url: `/v2/questionnaires/${questionnaireId}/questions`,
        params: {
            year,
        },
    });
};

/**
 * API call to fetch list of all answers
 *
 * @method fetchAnswerListAPI
 * @param   {number}    questionnaireId
 * @param   {number}    questionId
 * @param   {number}    id
 * @param   {number}    questionnaireStatusId
 * @return {Promise}
 */
export const fetchAnswerListAPI = ({ questionnaireId, questionId, id, questionnaireStatusId }) => {
    return axios({
        method: 'get',
        url: `/v2/questionnaires/${questionnaireId}/questions/${questionId}/answers`,
        params: {
            companyId: id,
            questionnaireStatusId,
            isActive: true,
        },
    });
};

/**
 * API call to create answer for a question
 *
 * @method createAnswerAPI
 * @param   {number}    questionnaireId
 * @param   {number}    questionId
 * @param   {Object}    data
 * @return {Promise}
 */
export const createAnswerAPI = ({ questionnaireId, questionId, data }) => {
    return axios({
        method: 'post',
        url: `/v2/questionnaires/${questionnaireId}/questions/${questionId}/answers`,
        data,
        headers: {
            'Content-Type': 'application/json',
        },
    });
};

/**
 * API call to update answer for a question
 *
 * @method updateAnswerAPI
 * @param   {number}    questionnaireId
 * @param   {number}    questionId
 * @param   {number}    id
 * @param   {Object}    data
 * @return {Promise}
 */
export const updateAnswerAPI = ({ questionnaireId, questionId, data, id }) => {
    return axios({
        method: 'put',
        url: `/v2/questionnaires/${questionnaireId}/questions/${questionId}/answers/${id}`,
        data,
        headers: {
            'Content-Type': 'application/json',
        },
    });
};

/**
 * API call to delete answer for a question
 *
 * @method deleteAnswerAPI
 * @param   {number}    questionnaireId
 * @param   {number}    questionId
 * @param   {number}    answerId
 * @return {Promise}
 */
export const deleteAnswerAPI = ({ questionnaireId, questionId, answerId }) => {
    return axios({
        method: 'delete',
        url: `/v2/questionnaires/${questionnaireId}/questions/${questionId}/answers/${answerId}`,
    });
};

/**
 * API call to fetch all answers
 *
 * @method fetchAllAnswersAPI
 * @param   {number}    questionnaireId
 * @param   {number}    companyId
 * @param   {number}    questionnaireStatusId
 * @param   {number}    year
 * @return {Promise}
 */
export const fetchAllAnswersAPI = ({ questionnaireId, companyId, questionnaireStatusId, year }) => {
    return axios({
        method: 'get',
        url: `v2/questionnaires/${questionnaireId}/answers`,
        params: {
            companyId,
            questionnaireStatusId,
            isActive: true,
            year,
        },
    });
};

/**
 * API call to fetch all Eligible Questionnaire Years
 *
 * @method fetchQuestionnaireEligibilityAPI
 * @return {Promise}
 */
export const fetchQuestionnaireEligibilityAPI = () => {
    return axios({
        method: 'get',
        url: `v2/aca-questionnaire-year-eligibility?limit=200`,
    });
};

/**
 * API call to fetch broker details, via `partnerId`.
 *
 * @method fetchBrokerDetailsAPI
 * @param  {number}     partnerId The partner to fetch
 * @return {Promise}
 */
export const fetchBrokerDetailsAPI = partnerId => {
    return axios({
        method: 'get',
        url: `/v2/partners/${partnerId}`,
    });
};

/**
 * API call to create Company Partner consent
 *
 * @method createConsentAPI
 * @param   {number}    companyId
 * @param   {Object}    data
 * @param   {number}    consentId
 * @return {Promise}
 */
export const createConsentAPI = ({ companyId, data, consentId }) => {
    let url = `/v2/companies/${companyId}/partner-consents/`;
    if (consentId) {
        url += `${consentId}`;
    }
    return axios({
        method: 'PUT',
        url,
        data,
        headers: {
            'Content-Type': 'application/json',
        },
    });
};

/**
 * API call to create Expert Service consent.
 *
 * @method createExpertServiceConsentAPI
 * @param   {number}    companyId
 * @param   {Object}    data
 * @return {Promise}
 */
export const createExpertServiceConsentAPI = ({ companyId, data }) => {
    return axios({
        method: 'post',
        url: `/v2/companies/${companyId}/aca-expert-consent`,
        data,
        headers: {
            'Content-Type': 'application/json',
        },
    });
};

/**
 * API call to fetch Expert Service consents.
 *
 * @method fetchExpertServiceConsentsAPI
 * @param   {number}    companyId
 * @param   {number|Array<number>} year
 * @param   {string}    consentStatus
 * @return {Promise}
 */
export const fetchExpertServiceConsentsAPI = ({ companyId, year, consentStatus }) => {
    return axios({
        method: 'get',
        url: `/v2/companies/${companyId}/aca-expert-consent`,
        params: {
            year: [].concat(year),
            isActive: true,
            ...(consentStatus && { consentStatus }),
        },
        paramsSerializer(params) {
            return Qs.stringify(params, { arrayFormat: 'repeat' });
        },
    });
};

/**
 * API call to update Expert Service consent.
 *
 * @method updateExpertServiceConsentAPI
 * @param   {number}    companyId
 * @param   {number}    consentId
 * @param   {Object}    data
 * @return {Promise}
 */
export const updateExpertServiceConsentAPI = ({ companyId, consentId, data }) => {
    return axios({
        method: 'put',
        url: `/v2/companies/${companyId}/aca-expert-consent/${consentId}`,
        data,
        headers: {
            'Content-Type': 'application/json',
        },
    });
};

/**
 * API call to fetch Company Partner consent details.
 *
 * @method fetchConsentDetailsAPI
 * @param  {number}     companyId
 * @param   {number}    year
 * @return {Promise}
 */
export const fetchConsentDetailsAPI = ({ companyId, year }) => {
    return axios({
        method: 'get',
        url: `/v2/companies/${companyId}/partner-consents`,
        params: {
            year,
            isActive: true,
        },
    });
};

/**
 * API call to fetch company additional data, via `companyId`.
 *
 * @method fetchCompanyAdditionalDataAPI
 * @param  {number}     companyId The partner to fetch
 * @return {Promise}
 */
export const fetchCompanyAdditionalDataAPI = companyId => {
    return axios({
        method: 'get',
        url: `/v2/companies/${companyId}/additional-data`,
    });
};

/**
 * API call to fetch Company Partner consents list.
 *
 * @method fetchConsentDetailsAPI
 * @param  {number}     companyId
 * @param   {Array}    consentStatus
 * @return {Promise}
 */
export const fetchPartnerConsentsListAPI = ({ companyId, consentStatus, params }) => {
    return axios({
        method: 'get',
        url: `/v2/companies/${companyId}/partner-consents`,
        params: {
            ...(consentStatus?.length > 0 && {
                consentStatus,
            }),
            ...params,
        },
        paramsSerializer: params => Qs.stringify(params, { arrayFormat: 'repeat' }),
    });
};

/**
 * API call to update answer for a question
 *
 * @method setACATaxYearInRedisAPI
 * @param   {Object}    data
 * @return {Promise}
 */
export const setACATaxYearInRedisAPI = ({ data }) => {
    return axios({
        method: 'post',
        url: `/v2/aca-reporting-data/aca-sso-year`,
        data,
        headers: {
            'Content-Type': 'application/json',
        },
    });
};
